import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/i18n";
import { usePageMeta, eur } from "@/lib/format";
import { CoachAvatars } from "./_shared";
import { toast } from "sonner";
import { ChevronLeft, Infinity as InfinityIcon, MessageCircle, Phone, Video, Check, ArrowRight, Loader2 } from "lucide-react";

const CREDIT_TO_CENTS = 10;

export default function CollabOfferings() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  usePageMeta(t("client_collab_offerings_title"));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/client/collab/offerings/available"); setRows(data?.offerings || []); }
    catch { setRows([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const doPurchase = async (offering) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data: r } = await api.post(`/client/collab/offerings/${offering.id}/purchase`, { payment_mode: "credits" });
      setConfirm(null);
      toast.success(t("client_collab_purchase_success_title"), { description: t("client_collab_purchase_success_body", { coaches: offering.coaches.map((c) => c.name).join(" + ") }) });
      await refreshUser?.().catch(() => {});
      await load();
      const chatId = r?.purchase?.shared_chat_id;
      if (chatId) navigate(`/app/shared-chat/${chatId}`);
    } catch (e) {
      let msg = apiError(e, t("client_collab_purchase_fail_body"));
      if (String(msg).toLowerCase().includes("not enough credits")) {
        const need = Math.max(1, Math.round(offering.price_cents / CREDIT_TO_CENTS));
        const have = Number(user?.credits || 0);
        msg = t("client_collab_purchase_credits_short", { need, have });
      }
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="client-collab-offerings">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/app/collab")} className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        <div>
          <h1 className="font-display text-2xl text-foreground">{t("client_collab_offerings_title")}</h1>
          <p className="text-sm text-muted-foreground">{t("client_collab_offerings_sub")}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : rows.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">{t("client_collab_offerings_empty")}</p>
      ) : rows.map((o) => (
        <div key={o.id} data-testid={`client-collab-offering-${o.id}`} className="flex gap-4 rounded-2xl border border-border bg-secondary/60 p-4">
          <CoachAvatars coaches={o.coaches} size={36} />
          <div className="min-w-0 flex-1">
            <p className="font-display text-base text-foreground">{o.title}</p>
            <p className="text-xs text-muted-foreground">{o.coaches.map((c) => c.name).join(" + ")}</p>
            {o.description ? <p className="mt-1.5 line-clamp-3 text-sm text-muted-foreground">{o.description}</p> : null}
            <div className="mt-2 flex flex-wrap gap-1.5">
              {o.unlimited ? <Chip icon={InfinityIcon} label={t("client_collab_included_unlimited")} /> : null}
              {o.included_messages ? <Chip icon={MessageCircle} label={`${o.included_messages} ${t("client_collab_included_msgs")}`} /> : null}
              {o.included_calls ? <Chip icon={Phone} label={`${o.included_calls} ${t("client_collab_included_calls")}`} /> : null}
              {o.has_live_sessions ? <Chip icon={Video} label={t("client_collab_included_live")} /> : null}
            </div>
            <div className="mt-3 flex items-center justify-between">
              <span className="font-display text-base text-primary">{eur(o.price_cents)}</span>
              {o.already_purchased ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1.5 text-sm font-medium text-emerald-700"><Check className="h-3.5 w-3.5" /> {t("client_collab_already_bought")}</span>
              ) : (
                <button data-testid={`client-collab-buy-${o.id}`} onClick={() => setConfirm(o)} disabled={busy}
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground">{t("client_collab_buy_cta")} <ArrowRight className="h-3.5 w-3.5" /></button>
              )}
            </div>
          </div>
        </div>
      ))}

      {confirm ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4" onClick={() => setConfirm(null)}>
          <div className="w-full max-w-md rounded-2xl bg-background p-5" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-display text-lg text-foreground">{t("client_collab_purchase_confirm_title")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("client_collab_purchase_confirm_body", { title: confirm.title, price: eur(confirm.price_cents) })}</p>
            <button data-testid="client-collab-pay-credits" onClick={() => doPurchase(confirm)} disabled={busy}
              className="mt-4 flex w-full items-center justify-center rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("client_collab_pay_credits", { n: Math.max(1, Math.round((confirm.price_cents || 0) / CREDIT_TO_CENTS)) })}
            </button>
            <button data-testid="client-collab-pay-cancel" onClick={() => setConfirm(null)} className="mt-2 w-full py-2 text-sm text-muted-foreground">{t("cancel")}</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Chip({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/5 px-2 py-0.5 text-[10px] font-medium text-primary"><Icon className="h-2.5 w-2.5" /> {label}</span>
  );
}
